import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import { IDog, IDogRunSummary, ISportProgress, IScentworkProgress, IScentworkLevelProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import { ManualRunEntryModal } from "../ManualRunEntry";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./ScentworkCard.scss";


interface IProps {
  dog: IDog;
  scentworkData: IScentworkProgress;
  onRunUpdated?: () => void;
}

const ScentworkCard: React.FC<IProps> = ({ dog, scentworkData, onRunUpdated }) => {
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<IDogRunSummary | undefined>(undefined);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(scentworkData.recent_runs || []);
  const deleteConfirmModal = useModal();
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  
  // Sync localRuns with scentworkData when it changes
  useEffect(() => {
    setLocalRuns(scentworkData.recent_runs || []);
  }, [scentworkData.recent_runs]);

  // Render title badges (earned titles)
  const renderTitleBadge = (title: { title_code: string; title_name: string; earned_date: Date; color_hex?: string }) => {
    return (
      <div 
        key={title.title_code} 
        className="titleBadge"
        style={{ backgroundColor: title.color_hex || '#4CAF50' }}
      >
        <div className="badgeInfo">
          <div className="badgeTitle">{title.title_code}</div>
          <div className="badgeDate">{moment(title.earned_date).format('M/D/YY')}</div>
        </div>
      </div>
    );
  };

  // Render element progress cell for Odor Search elements
  const renderElementCell = (level: IScentworkLevelProgress, elementName: string) => {
    const element = level.elements.find(e => e.element === elementName);
    if (!element) return null;

    const isComplete = element.title_earned;
    const progress = `${element.qualified_count}/${element.required_count}`;

    return (
      <div 
        key={`${level.level}-${elementName}`}
        className={`elementCell ${isComplete ? 'complete' : ''}`}
        title={`${level.level_display} ${element.element_display}: ${progress} Qs`}
      >
        {isComplete ? (
          <div className="titleCode">{element.title_code}</div>
        ) : (
          <div className="progress">{progress}</div>
        )}
      </div>
    );
  };

  // Render Handler Discrimination cell for a level
  const renderHandlerDiscCell = (levelName: string) => {
    const hdLevel = scentworkData.handler_discrimination.levels.find(l => l.element === levelName);
    if (!hdLevel) return <div className="elementCell">-</div>;

    const isComplete = hdLevel.title_earned;
    const progress = `${hdLevel.qualified_count}/${hdLevel.required_count}`;

    return (
      <div 
        className={`elementCell ${isComplete ? 'complete' : ''}`}
        title={`Handler Discrimination ${hdLevel.element_display}: ${progress} Qs`}
      >
        {isComplete ? (
          <div className="titleCode">{hdLevel.title_code}</div>
        ) : (
          <div className="progress">{progress}</div>
        )}
      </div>
    );
  };

  // Render level row in the grid (Odor Search + Handler Disc)
  const renderLevelRow = (level: IScentworkLevelProgress) => {
    return (
      <div key={level.level} className="levelRow">
        <div className="levelLabel">{level.level_display}</div>
        {/* Odor Search elements */}
        {[
          Constants.dog_class_element.scentwork_container,
          Constants.dog_class_element.scentwork_interior,
          Constants.dog_class_element.scentwork_exterior,
          Constants.dog_class_element.scentwork_buried
        ].map(element => 
          renderElementCell(level, element)
        )}
        {/* Handler Discrimination */}
        {renderHandlerDiscCell(level.level)}
        {/* Level Title */}
        <div className="levelTitleCell">
          {level.level_title_earned ? (
            <span className="levelTitle" title={`Scent Work ${level.level_display}`}>
              {level.level_title_code}
            </span>
          ) : (
            <span className="incomplete">-</span>
          )}
        </div>
      </div>
    );
  };

  // Render Detective row
  const renderDetectiveRow = () => {
    const detective = scentworkData.detective;
    const isComplete = detective.title_earned;
    const progress = `${detective.qualified_count}/${detective.required_count}`;

    return (
      <div className="levelRow detectiveRow">
        <div className="levelLabel">Detective</div>
        {/* Empty cells for Odor Search elements */}
        <div className="elementCell empty">-</div>
        <div className="elementCell empty">-</div>
        <div className="elementCell empty">-</div>
        <div className="elementCell empty">-</div>
        {/* Detective progress */}
        <div 
          className={`elementCell detectiveCell ${isComplete ? 'complete' : ''}`}
          title={`Detective class: ${progress} Qs`}
        >
          {isComplete ? (
            <div className="titleCode">{detective.title_code}</div>
          ) : (
            <div className="progress">{progress}</div>
          )}
        </div>
        {/* Empty level title cell */}
        <div className="levelTitleCell">-</div>
      </div>
    );
  };

  // Handle run actions
  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    setIsModalOpen(true);
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef<string | null>(null); // ScentworkCard has no deleting state
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });

  const confirmDelete = async () => {
    if (!runToDelete) return;
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        type: "success",
        title: "Run deleted successfully",
        content: "",
        duration: 5000
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const scentworkSport = response.sports.find((s: ISportProgress) => s.show_type === 'Scentwork');
      if (scentworkSport) {
        setLocalRuns(scentworkSport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
      deleteConfirmModal.toggle();
    } catch (error) {
      console.error("Error deleting run:", error);
      toast.show({
        type: "fail",
        title: "Failed to delete run",
        content: "",
        duration: 5000
      });
    } finally {
      setRunToDelete(null);
    }
  };

  const handleAddRun = () => {
    setEditingRun(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRun(undefined);
  };

  const handleRunSaved = async () => {
    setIsModalOpen(false);
    setEditingRun(undefined);
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const scentworkSport = response.sports.find((s: ISportProgress) => s.show_type === 'Scentwork');
      if (scentworkSport) {
        setLocalRuns(scentworkSport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleCloseRunsModal = () => {
    setShowRunsModal(false);
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Class',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Element',
      field: 'element',
      valueFormatter: (params) => params.value || '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Result',
      field: 'result_status',
      valueFormatter: (params) => params.value || '-',
      cellStyle: (params) => {
        if (params.value === Constants.run_result_status.qualified) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value === Constants.run_result_status.not_qualified) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Time',
      field: 'time_seconds',
      valueFormatter: (params) => {
        if (!params.value) return '-';
        const minutes = Math.floor(params.value / 60);
        const seconds = (params.value % 60).toFixed(2);
        return `${minutes}:${seconds.padStart(5, '0')}`;
      },
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Event',
      field: 'event_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 200,
      flex: 2
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 100,
      flex: 1
    }
  ];

  // Render run row in the runs modal - UNUSED (converted to AGGrid)
  /*
  const renderRunRow = (run: IDogRunSummary) => {
    const isManualEntry = !run.event; // Manual entries don't have event_id

    const menuOptions: IContextMenuOption[] = [
      {
        label: 'Edit',
        onClick: () => handleEditRun(run)
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteRun(run._id)
      }
    ];

    return (
      <tr key={run._id}>
        <td className="dateColumn">{moment(run.trial_date).format('M/D/YY')}</td>
        <td className="elementColumn">{run.element}</td>
        <td className="levelColumn">{run.level}</td>
        <td className="resultColumn">
          <span className={`resultBadge ${run.result_status?.toLowerCase()}`}>
            {run.result_status}
          </span>
        </td>
        <td className="eventColumn hideOnMobile">{run.event_name || run.event?.name || '-'}</td>
        <td className="actionsColumn">
          {run.verified ? (
            <div className="verifiedBadge">
              <img src={PawIcon} alt="Verified" className="pawIcon" />
              <span>Verified</span>
            </div>
          ) : isManualEntry ? (
            <ContextMenu options={menuOptions} />
          ) : null}
        </td>
      </tr>
    );
  };
  */

  return (
    <div className="scentworkCard">
      {/* Header */}
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>AKC Scent Work</h3>
          <p className="cardSubtitle">Detection & Scent Work</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{scentworkData.total_element_titles + scentworkData.total_level_titles + scentworkData.total_handler_disc_titles + (scentworkData.detective_title_earned ? 1 : 0)}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Qualified Runs</div>
              <div className="statValue">{scentworkData.total_qs}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {scentworkData.earned_titles && scentworkData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <h4>Earned Titles ({scentworkData.earned_titles.length})</h4>
          <div className="titleBadges">
            {scentworkData.earned_titles.map(renderTitleBadge)}
          </div>
        </div>
      )}

      {/* Progress Grid */}
      <div className="progressGrid">
        <div className="gridHeader">
          <div className="levelHeader">Level</div>
          <div className="elementHeader">Container</div>
          <div className="elementHeader">Interior</div>
          <div className="elementHeader">Exterior</div>
          <div className="elementHeader">Buried</div>
          <div className="elementHeader">Handler Disc.</div>
          <div className="levelTitleHeader">Level Title</div>
        </div>
        {scentworkData.levels.map(renderLevelRow)}
        {renderDetectiveRow()}
      </div>

      {/* Last Run Info */}
      {scentworkData.recent_runs && scentworkData.recent_runs.length > 0 && scentworkData.recent_runs[0] && (
        <div className="lastRunSection">
          <h4>Last Run - {moment(scentworkData.recent_runs[0].trial_date).format('MMMM Do, YYYY')}</h4>
          <div className="lastRunInfo">
            <span className="lastRunMetric">
              <span className="metricLabel">Element:</span> <span className="metricValue">{scentworkData.recent_runs[0].element}</span>
            </span>
            <span className="lastRunMetric">
              <span className="metricLabel">Level:</span> <span className="metricValue">{scentworkData.recent_runs[0].level}</span>
            </span>
            <span className="lastRunMetric">
              <span className="metricLabel">Result:</span> <span className="metricValue">{scentworkData.recent_runs[0].result_status}</span>
            </span>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="cardDivider"></div>

      {/* View Runs Button */}
      {/* View Run History Link - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View & Edit Run History ({scentworkData.recent_runs?.length || 0} runs)
          </button>
        </div>
      ) : scentworkData.recent_runs && scentworkData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View Run History ({scentworkData.recent_runs.length} runs)
          </button>
        </div>
      ) : null}

      {/* Runs Modal */}
      <Modal
        isShown={showRunsModal}
        hide={handleCloseRunsModal}
        className="runsHistoryModal"
        modalContent={
          <div className="runsModal">
            <h2>AKC Scent Work - Run History</h2>

            <div className="runsModalContent">
              <div className="runsSection">
                <div className="runsSectionHeader">
                  <h4>All Runs</h4>
                </div>
                {scentworkData.recent_runs.length === 0 ? (
                  <div className="noRuns">
                    <p>No runs recorded yet.</p>
                  </div>
                ) : (
                  <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                      rowData={localRuns}
                      columnDefs={columnDefs}
                      domLayout='normal'
                      suppressCellFocus={true}
                      theme={themeQuartz}
                      getRowId={(params) => params.data._id}
                    />
                  </div>
                )}
              </div>

              <div className="modalActions">
                <Button onClick={handleAddRun}>
                  + Add Manual Run
                </Button>
              </div>
            </div>
          </div>
        }
      />

      {/* Manual Run Entry Modal */}
      <ManualRunEntryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        dog={dog}
        sportType={Constants.show_type.scentwork}
        sanctioningClub={Constants.sanctioning_club.AKC}
        onSuccess={handleRunSaved}
        editRun={editingRun as any}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isShown={deleteConfirmModal.isShown}
        hide={deleteConfirmModal.toggle}
        modalContent={
          <div className="deleteConfirmModal">
            <h2>Delete Run</h2>
            <p>Are you sure you want to delete this run? This will recalculate your title progress.</p>
            <div className="modalActions">
              <Button onClick={deleteConfirmModal.toggle} variant="secondary">
                Cancel
              </Button>
              <Button onClick={confirmDelete} className="deleteButton">
                Delete
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default ScentworkCard;

